import { EVENTS_PAGE_SIZE } from '@entities/event/lib/hooks/useFetchEvents';
import { getRouteEventCreate } from '@shared/constants/router';
import { SminexPlugIcon } from '@shared/assets/icons';
import { BasicButton } from '@shared/ui/basic-button';
import { EventListItem } from '../EventListItem';
import { useNavigate } from 'react-router-dom';
import styles from './event-list.module.scss';
import { useCallback, useRef } from 'react';
import { Event } from '@entities/event';

interface EventListProps {
  events: Event[];
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  groupName?: string | null;
}

const getSkeletons = (pageSize: number) =>
  new Array(pageSize).fill(0).map((_, index) => <div key={index} className={styles.loader} />);

export const EventList = ({ events, isLoading, hasMore, loadMore, groupName }: EventListProps) => {
  const navigate = useNavigate();
  const observerRef = useRef<IntersectionObserver>();

  const lastEventElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading) return;

      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      });

      if (node) {
        observerRef.current.observe(node);
      }
    },
    [isLoading, hasMore, loadMore]
  );

  const handleNavigate = () => {
    navigate(getRouteEventCreate());
  };

  if (!isLoading && !events.length) {
    return (
      <div className={styles.container}>
        <SminexPlugIcon />
        <p className={styles.title}>Нет мероприятий</p>
        <p className={styles.subtitle}>Нажмите кнопку ниже, чтобы добавить первое мероприятие</p>
        <BasicButton onClick={handleNavigate}>Создать</BasicButton>
      </div>
    );
  }

  return (
    <>
      {events.map((event, index) => (
        <EventListItem
          key={event.id}
          event={event}
          ref={index === events.length - 1 ? lastEventElementRef : null}
          groupName={groupName}
        />
      ))}

      {isLoading && getSkeletons(EVENTS_PAGE_SIZE)}
    </>
  );
};
